import React, { useEffect, useMemo, useRef, useState } from "react";
import ProxyTable from "@components/ProxyTable";
import AddProxiesModal from "@components/AddProxiesModal";
import axios from "axios";

/** ---------- helpers ---------- */
function detectProxyState(r) {
  // под разные схемы: status/isAlive/alive/ok/lastCheckOk
  const st = String(r?.status || "").toLowerCase();

  if (r?.isAlive === true || r?.alive === true || r?.ok === true) return "alive";
  if (r?.isAlive === false || r?.alive === false || r?.ok === false) return "inactive";

  if (st.includes("alive") || st.includes("ok") || st.includes("good") || st === "working")
    return "alive";
  if (st.includes("inactive") || st.includes("bad") || st.includes("fail") || st === "error")
    return "inactive";

  // если статуса нет — считаем unknown
  return "unknown";
}

function FilterDropdown({ open, anchorRef, value, onChange, onClose }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      const panel = panelRef.current;
      const anchor = anchorRef?.current;
      if (!panel) return;

      if (panel.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      onClose?.();
    };

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const box =
    "rounded-[16px] border border-[#E5E7EB] bg-white shadow-[0_18px_40px_rgba(0,0,0,0.12)]";
  const hint = "text-[11px] text-[#6B7280]";
  const item =
    "px-3 py-2 rounded-[14px] border border-[#E5E7EB] hover:bg-[#F9FAFB] transition cursor-pointer flex items-center gap-2";

  return (
    <div className={`absolute right-0 top-[48px] w-[320px] ${box} p-4`} ref={panelRef}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[14px] font-semibold text-[#111827]">Фильтр</div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-[#F3F4F6] grid place-items-center hover:bg-[#EDEFF3] transition"
          title="Закрыть"
        >
          <i className="fa-solid fa-xmark text-[#111827]" />
        </button>
      </div>

      <div className="mt-4 grid gap-3">
        <label className={item}>
          <input
            type="radio"
            name="proxyFilter"
            checked={value.state === "all"}
            onChange={() => onChange({ ...value, state: "all" })}
            className="accent-[#2B7CFF]"
          />
          <span className="text-[13px] text-[#111827]">Все</span>
        </label>

        <label className={item}>
          <input
            type="radio"
            name="proxyFilter"
            checked={value.state === "alive"}
            onChange={() => onChange({ ...value, state: "alive" })}
            className="accent-[#2B7CFF]"
          />
          <span className="text-[13px] text-[#111827]">Активные</span>
        </label>

        <label className={item}>
          <input
            type="radio"
            name="proxyFilter"
            checked={value.state === "inactive"}
            onChange={() => onChange({ ...value, state: "inactive" })}
            className="accent-[#2B7CFF]"
          />
          <span className="text-[13px] text-[#111827]">Мертвые</span>
        </label>

        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => onChange({ state: "all" })}
            className={`h-10 px-4 rounded-full border border-[#D6D9E0] bg-white hover:bg-[#F7F8FA] transition text-sm font-medium ${
              value.state === "all" ? "opacity-50 pointer-events-none" : ""
            }`}
          >
            Сбросить
          </button>

          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-full bg-[#2B7CFF] text-white shadow-[0_12px_28px_rgba(43,124,255,0.35)] hover:brightness-95 transition text-sm font-semibold"
          >
            Применить
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Proxy() {
  const [rows, setRows] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [query, setQuery] = useState("");

  // dropdown filter
  const [filterOpen, setFilterOpen] = useState(false);
  const filterBtnRef = useRef(null);
  const [filters, setFilters] = useState({ state: "all" }); // all | alive | dead

  async function load() {
    const res = await fetch("/api/proxies");
    const data = await res.json();
    setRows(Array.isArray(data) ? data : []);
  }

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return rows.filter((r) => {
      // 1) filter by state (если у тебя нет статуса, можно удалить этот блок)
      if (filters.state !== "all") {
        const st = detectProxyState(r);
        if (filters.state === "alive" && st !== "alive") return false;
        if (filters.state === "inactive" && st !== "inactive") return false;
      }

      // 2) search by uid / proxyUrl
      if (!q) return true;

      const uidStr = String(r.uid ?? "").toLowerCase();
      const proxyStr = String(r.proxyUrl ?? "").toLowerCase();

      return uidStr.includes(q) || proxyStr.includes(q);
    });
  }, [rows, query, filters]);

  const filterBadgeCount = useMemo(() => (filters.state !== "all" ? 1 : 0), [filters]);

  function onAdded() {
    load().catch(() => {});
  }

  async function editOne(uid) {
    const raw = window.prompt(
      `Вставь новый прокси для uid=${uid}\nФормат:\n- ip:port:user:pass\n- или http://user:pass@ip:port`
    );
    if (!raw) return;

    try {
      const { data } = await axios.put(`/api/proxies/${uid}`, { proxyUrl: raw });

      if (!data.success) {
        alert(data.message || "Не удалось обновить прокси");
        return;
      }

      alert(`✅ Прокси uid=${uid} обновлён. Статус: ${data.status}`);
      await load().catch(() => {});
    } catch (e) {
      console.error(e);
      alert("Ошибка при обновлении прокси");
    }
  }

  async function recheckOne(uid) {
    try {
      const { data } = await axios.post(`/api/proxies/${uid}/recheck`);

      if (!data.success) {
        alert(data.message || "Не удалось перепроверить прокси");
        return;
      }

      alert(`✅ Прокси uid=${uid} перепроверен. Статус: ${data.status}`);
      await load().catch(() => {});
    } catch (e) {
      console.error(e);
      alert("Ошибка при перепроверке прокси");
    }
  }

  return (
    <div className="min-h-screen">
      <main className="px-6 pb-14">
        <div className="mx-auto max-w-[1220px]">
          {/* Заголовок + контролы */}
          <div className="mt-10 flex items-center justify-between gap-6">
            <h1 className="text-[28px] font-semibold text-[#111827]">Список прокси</h1>

            <div className="flex items-center gap-3 relative">
              {/* Search */}
              <div className="h-10 w-[360px] border border-[#C8C8C8] bg-white rounded-full shadow-[0_10px_25px_rgba(0,0,0,0.06)] px-4 flex items-center gap-2">
                <i className="fa-solid fa-magnifying-glass text-[#9CA3AF] w-6" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full outline-none text-sm placeholder:text-[#9CA3AF]"
                  placeholder="Поиск по uid / proxy"
                />
              </div>

              {/* Filter */}
              <button
                ref={filterBtnRef}
                onClick={() => setFilterOpen((v) => !v)}
                className="relative h-10 px-4 border border-[#C8C8C8] bg-white rounded-full shadow-[0_10px_25px_rgba(0,0,0,0.06)] flex items-center gap-2 text-sm font-medium text-[#111827] hover:opacity-90 transition"
                type="button"
              >
                <i className="fa-solid fa-arrow-up-short-wide h-3" />
                Фильтр
                {filterBadgeCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-[#EEF2FF] text-[#3730A3] border border-[#C7D2FE]">
                    {filterBadgeCount}
                  </span>
                )}
              </button>

              <FilterDropdown
                open={filterOpen}
                anchorRef={filterBtnRef}
                value={filters}
                onChange={setFilters}
                onClose={() => setFilterOpen(false)}
              />
            </div>
          </div>

          <div className="mt-3 text-[12px] text-[#6B7280]">
            Найдено: <span className="font-semibold text-[#111827]">{filtered.length}</span>
            {query.trim() && (
              <>
                {" "}
                • запрос: <span className="font-semibold text-[#111827]">"{query.trim()}"</span>
              </>
            )}
            {filters.state !== "all" && (
              <>
                {" "}
                • фильтр:{" "}
                <span className="font-semibold text-[#111827]">{filters.state}</span>
              </>
            )}
          </div>

          {/* Таблица */}
          <div className="mt-7">
            <ProxyTable rows={filtered} onEditOne={editOne} onRecheckOne={recheckOne} />
          </div>
        </div>
      </main>

      <AddProxiesModal open={openAdd} onClose={() => setOpenAdd(false)} onAdded={onAdded} />
    </div>
  );
}
